import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { API_BASE_URL } from '../../config';
import { User, Restaurant, Review, Vote } from '../../types';

interface AuthState {
  token: string | null;
  refreshToken: string | null;
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}

interface AuthResponse {
  access: string;
  refresh: string;
  user: User;
}

const initialState: AuthState = {
  token: localStorage.getItem('token'),
  refreshToken: localStorage.getItem('refreshToken'),
  user: null,
  isAuthenticated: false,
  loading: false,
  error: null,
};

export const login = createAsyncThunk<AuthResponse, { email: string; password: string }>(
  'auth/login',
  async (credentials) => {
    console.log('Making login API call...');
    const response = await axios.post<AuthResponse>(`${API_BASE_URL}/api/auth/login/`, credentials);
    console.log('Login API response:', response.data);
    
    // Ensure we have the required fields
    if (!response.data.access) {
      throw new Error('Invalid login response: missing access token');
    }
    
    // Store the token
    const token = response.data.access;
    localStorage.setItem('token', token);
    if (response.data.refresh) {
      localStorage.setItem('refreshToken', response.data.refresh);
    }
    
    return response.data;
  }
);

export const register = createAsyncThunk<AuthResponse, { email: string; password: string; first_name: string; last_name: string }>(
  'auth/register',
  async (userData) => {
    const response = await axios.post<AuthResponse>(`${API_BASE_URL}/api/auth/register/`, userData);
    return response.data;
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { getState }) => {
    const state = getState() as { auth: AuthState };
    const { refreshToken } = state.auth;
    
    if (refreshToken) {
      try {
        await axios.post(`${API_BASE_URL}/api/auth/logout/`, { refresh: refreshToken });
      } catch (error) {
        console.error('Error during logout:', error);
      }
    }
    
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    return null;
  }
);

export const fetchUser = createAsyncThunk<User>(
  'auth/fetchUser',
  async (_, { getState }) => {
    const state = getState() as { auth: AuthState };
    const { token } = state.auth;
    
    if (!token) {
      console.error('No token available for fetching user data');
      throw new Error('No token available');
    }
    
    console.log('Fetching user data with token:', token);
    try {
      // Ensure token is properly formatted
      const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
      console.log('Using auth token:', authToken);
      
      const response = await axios.get<User>(`${API_BASE_URL}/api/users/me/`, {
        headers: {
          'Authorization': authToken,
          'Content-Type': 'application/json'
        }
      });
      console.log('User data response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error fetching user data:', error.response?.data || error.message);
      if (error.response?.status === 401) {
        // If unauthorized, clear the token and redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        throw new Error('Session expired. Please login again.');
      }
      throw error;
    }
  }
);

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Login
      .addCase(login.pending, (state) => {
        console.log('Login pending...');
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        console.log('Login fulfilled with payload:', action.payload);
        
        if (!action.payload.access || !action.payload.refresh || !action.payload.user) {
          console.error('Invalid login response:', action.payload);
          state.error = 'Invalid login response from server';
          state.loading = false;
          return;
        }

        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.access;
        state.refreshToken = action.payload.refresh;
        state.user = action.payload.user;
        
        // Store tokens in localStorage
        localStorage.setItem('token', action.payload.access);
        localStorage.setItem('refreshToken', action.payload.refresh);
        
        console.log('Updated Redux state:', {
          isAuthenticated: state.isAuthenticated,
          token: state.token,
          user: state.user
        });
      })
      .addCase(login.rejected, (state, action) => {
        console.log('Login rejected:', action.error);
        state.loading = false;
        state.error = action.error.message || 'Login failed';
      })
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        state.isAuthenticated = true;
        state.token = action.payload.access;
        state.refreshToken = action.payload.refresh;
        state.user = action.payload.user;
        localStorage.setItem('token', action.payload.access);
        localStorage.setItem('refreshToken', action.payload.refresh);
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Registration failed';
      })
      // Logout
      .addCase(logout.fulfilled, (state) => {
        state.token = null;
        state.refreshToken = null;
        state.user = null;
        state.isAuthenticated = false;
      })
      // Fetch User
      .addCase(fetchUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
      })
      .addCase(fetchUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch user data';
      });
  },
});

export default authSlice.reducer; 